import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useClinicSearch
 * Central data-fetching and filter-state hook for the clinic search UI.
 *
 * @param {string} apiBase  Base URL of the REST API (e.g. /wp-json/clinic-search/v1)
 */
export default function useClinicSearch(apiBase) {
  // ── Filter state ──────────────────────────────────────────────────────────
  const [searchTerm,       setSearchTermRaw]   = useState('');
  const [debouncedSearch,  setDebouncedSearch] = useState('');
  const [selectedRegion,   setSelectedRegion]  = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);

  // ── Data state ────────────────────────────────────────────────────────────
  const [clinics,       setClinics]    = useState([]);
  const [regions,       setRegions]    = useState([]);
  const [provinces,     setProvinces]  = useState([]);
  const [allProvinces,  setAllProvinces] = useState([]); // full list for cascading
  const [products,      setProducts]   = useState([]);

  // ── Global plugin settings (one-time fetch) ──────────────────────────────
  // hero_bg_url: string|null — admin-selected hero background image, or null
  // when admin hasn't configured one (frontend falls back to gradient).
  const [heroBgUrl, setHeroBgUrl] = useState(null);

  // ── Pagination state ──────────────────────────────────────────────────────
  const [currentPage,  setCurrentPage]  = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalClinics, setTotalClinics] = useState(0);
  // 9 = clean 3×3 desktop grid. Caps render cost: even with 100+ clinics
  // we only ever mount 9 cards at a time + paginate the rest server-side.
  const perPage = 9;

  // ── UI state ──────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // ── Geolocation state ─────────────────────────────────────────────────────
  const [nearMeActive,  setNearMeActive]  = useState(false);
  const [userLocation,  setUserLocation]  = useState(null); // {lat, lng}
  const [geoLoading,    setGeoLoading]    = useState(false);
  const [geoError,      setGeoError]      = useState(null);

  // AbortController ref for in-flight request cancellation
  const abortRef = useRef(null);

  // ── Debounce search input (400ms) ─────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1); // reset to page 1 on new search
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ── Fetch static data on mount ────────────────────────────────────────────
  useEffect(() => {
    fetchRegions();
    fetchAllProvinces();
    fetchProducts();
    fetchSettings();
  }, [apiBase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cascade provinces when region changes ─────────────────────────────────
  // Region UI is back in v1.0.4 (Touch reverted the removal). Behaviour mirrors
  // the original v1.0.1+ flow:
  //   - selectedRegion = '' / 0  → show ALL provinces (use cached allProvinces)
  //   - selectedRegion > 0       → re-fetch /provinces?region_id=X
  // Province selection is cleared whenever the region actually changes, so the
  // user never lands on a province that isn't part of the new region.
  useEffect(() => {
    let cancelled = false;

    if (selectedRegion === '' || selectedRegion === 0 || selectedRegion === '0') {
      // No region filter — use the full cached list (no extra request).
      setProvinces(allProvinces);
      return () => { cancelled = true; };
    }

    // Region selected → fetch the filtered list from the API.
    (async () => {
      try {
        const res  = await fetch(`${apiBase}/provinces?region_id=${encodeURIComponent(selectedRegion)}`);
        const data = await res.json();
        if (cancelled) return;
        setProvinces(Array.isArray(data) ? data : []);
      } catch {
        if (cancelled) return;
        // Fallback: client-side filter from the cached list using region_id term meta
        const filtered = allProvinces.filter(
          (p) => String(p.region_id) === String(selectedRegion)
        );
        setProvinces(filtered);
      }
    })();

    // Clear province selection whenever a region is set (user is reselecting)
    setSelectedProvince('');

    return () => { cancelled = true; };
  }, [selectedRegion, allProvinces, apiBase]);

  // ── Fetch clinics whenever filters/pagination/geo change ─────────────────
  useEffect(() => {
    fetchClinics();
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    debouncedSearch,
    selectedRegion,
    selectedProvince,
    selectedProducts,
    currentPage,
    apiBase,
    nearMeActive,
    userLocation,
  ]);

  // ── Reset page when filters change ───────────────────────────────────────
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedRegion, selectedProvince, selectedProducts]);

  // ── Fetch functions ───────────────────────────────────────────────────────

  async function fetchRegions() {
    try {
      const res  = await fetch(`${apiBase}/regions`);
      const data = await res.json();
      setRegions(Array.isArray(data) ? data : []);
    } catch {
      // Non-critical — silently fail
    }
  }

  async function fetchAllProvinces() {
    try {
      const res  = await fetch(`${apiBase}/provinces`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setAllProvinces(list);
      setProvinces(list);
    } catch {
      // Non-critical — silently fail
    }
  }

  async function fetchProducts() {
    try {
      const res  = await fetch(`${apiBase}/products`);
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch {
      // Non-critical — silently fail
    }
  }

  // Fetch plugin-level settings (currently just hero_bg_url).
  // Called once on mount; gracefully no-ops on failure so the UI still renders
  // with its fallback gradient hero.
  async function fetchSettings() {
    try {
      const res  = await fetch(`${apiBase}/settings`);
      if (!res.ok) return;
      const data = await res.json();
      // Expected shape: { hero_bg_url: string | null }
      const url = data && typeof data.hero_bg_url === 'string' && data.hero_bg_url
        ? data.hero_bg_url
        : null;
      setHeroBgUrl(url);
    } catch {
      // Non-critical — fallback to gradient hero
    }
  }

  async function fetchClinics() {
    // Cancel any in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      // Build query params
      const params = new URLSearchParams();
      params.set('page',     currentPage);
      params.set('per_page', perPage);

      if (debouncedSearch)  params.set('search',   debouncedSearch);
      if (selectedRegion)   params.set('region',   selectedRegion);
      if (selectedProvince) params.set('province', selectedProvince);

      // For multi-product filter: send one at a time (API handles single product_id)
      // If multiple products selected, we send first selected — UI shows all selected as chips
      // For true multi-product OR filtering, we do client-side intersection if needed
      if (selectedProducts.length === 1) {
        params.set('product_id', selectedProducts[0]);
      }
      // Multi-product: fetch without product filter, filter client-side
      // This is acceptable for moderate data sizes; server-side multi requires UNION queries

      const url = `${apiBase}/clinics?${params.toString()}`;
      const res = await fetch(url, { signal: abortRef.current.signal });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();

      let clinicList = data.clinics ?? [];

      // Client-side multi-product filter (when 2+ products selected)
      if (selectedProducts.length > 1) {
        clinicList = clinicList.filter((clinic) => {
          const clinicProductIds = (clinic.products ?? []).map((p) => p.product_id);
          // Clinic must carry AT LEAST ONE of the selected products
          return selectedProducts.some((pid) => clinicProductIds.includes(Number(pid)));
        });
      }

      // Apply geo distance + sort when near-me is active
      if (nearMeActive && userLocation) {
        clinicList = clinicList
          .map((clinic) => ({
            ...clinic,
            distance:
              clinic.lat && clinic.lng
                ? haversineKm(userLocation.lat, userLocation.lng, clinic.lat, clinic.lng)
                : null,
          }))
          .sort((a, b) => {
            if (a.distance === null && b.distance === null) return 0;
            if (a.distance === null) return 1;
            if (b.distance === null) return -1;
            return a.distance - b.distance;
          });
      }

      setClinics(clinicList);
      setTotalClinics(data.pagination?.total ?? clinicList.length);
      setTotalPages(data.pagination?.total_pages ?? 1);
    } catch (err) {
      if (err.name === 'AbortError') return; // Request was cancelled — not an error
      console.error('[ClinicSearch] fetch error:', err);
      setError('ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
      setClinics([]);
    } finally {
      setLoading(false);
    }
  }

  // ── Haversine distance (km) ───────────────────────────────────────────────

  function haversineKm(lat1, lng1, lat2, lng2) {
    const R    = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a    = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // ── Geolocation: request near-me ─────────────────────────────────────────

  const requestNearMe = useCallback(() => {
    // Toggle off
    if (nearMeActive) {
      setNearMeActive(false);
      setUserLocation(null);
      setGeoError(null);
      return;
    }

    // Geolocation requires HTTPS — blocked on plain HTTP
    if (!window.isSecureContext) {
      setGeoError('ต้องเปิดผ่าน HTTPS เพื่อใช้งาน GPS (เปลี่ยนเป็น https://)');
      return;
    }

    if (!navigator.geolocation) {
      setGeoError('เบราว์เซอร์ไม่รองรับ GPS');
      return;
    }

    setGeoLoading(true);
    setGeoError(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setNearMeActive(true);
        setGeoLoading(false);
      },
      (err) => {
        const msg = err.code === 1 ? 'กรุณาอนุญาตการเข้าถึง GPS'
                  : err.code === 2 ? 'ไม่สามารถระบุตำแหน่งได้'
                  : 'เกิดข้อผิดพลาด กรุณาลองใหม่';
        setGeoError(msg);
        setGeoLoading(false);
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  }, [nearMeActive]);

  // ── Clear all filters ─────────────────────────────────────────────────────

  const clearFilters = useCallback(() => {
    setSearchTermRaw('');
    setDebouncedSearch('');
    setSelectedRegion('');
    setSelectedProvince('');
    setSelectedProducts([]);
    setCurrentPage(1);
  }, []);

  // ── Wrapped setters that also reset page ─────────────────────────────────

  const setSearchTerm = useCallback((val) => {
    setSearchTermRaw(val);
  }, []);

  return {
    // Data
    clinics,
    regions,
    provinces,
    products,
    // Filter state
    searchTerm,
    selectedRegion,
    selectedProvince,
    selectedProducts,
    // Pagination
    currentPage,
    totalPages,
    totalClinics,
    perPage,
    // Loading / error
    loading,
    error,
    // Setters
    setSearchTerm,
    setSelectedRegion,
    setSelectedProvince,
    setSelectedProducts,
    setCurrentPage,
    clearFilters,
    // Geolocation
    nearMeActive,
    geoLoading,
    geoError,
    requestNearMe,
    // Settings
    heroBgUrl,
  };
}
